// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {useNodeValue} from '@wandb/weave/react';
import * as _ from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {capitalizeFirstLetter} from '../../../../util/string';
import {SelectEntity} from '../../../Launch/Queues/SelectEntity';
import {
  EntityOptions,
  ProjectOption,
  SelectProject,
} from '../../../Launch/SelectProject2';
import {getArtifactTypesNode} from '../../../ModelRegistry/utils';
import {
  InstrumentedLoader,
  SemanticLoader as Loader,
} from '../../../utility/InstrumentedLoader';
import {REGISTRY_PROJECT_PREFIX} from '../../common/utils';
import {AddVersionHelpBanner} from './AddVersionHelpBanner';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ArtifactVersionTTLBanner} from './ArtifactVersionTTLBanner';
import {Artifact, Collection} from './CreateRegisteredCollectionDrawerInner';
import * as S from './CreateRegisteredCollectionDrawerInner.styles';
import {
  ArtifactCollectionOption,
  SelectArtifactCollection,
} from './SelectArtifactCollection';
import {
  ArtifactVersion,
  ArtifactVersionOption,
  SelectArtifactCollectionVersion,
} from './SelectArtifactCollectionVersion';
import {SubText} from './SubText';
import {getEntityProjects, getProjectArtifactSequences} from './utils';

type DrawerPageAddVersionProps = {
  collection: Collection;
  defaultProjectName: string;
  artifact: Artifact;
  artifactVersions: ArtifactVersion[];
  hideStepHeader?: boolean;
  hideBanner?: boolean;
  setArtifact: (val: Artifact) => void;
  setArtifactVersions: (val: ArtifactVersion[]) => void;
};

export const DrawerPageAddVersion = ({
  collection,
  defaultProjectName,
  artifact,
  artifactVersions,
  hideStepHeader,
  hideBanner,
  setArtifact,
  setArtifactVersions,
}: DrawerPageAddVersionProps) => {
  const isOrgRegistry = defaultProjectName.startsWith(REGISTRY_PROJECT_PREFIX);

  // Does this entity have logged versions?
  const typesNode = useMemo(() => {
    return getArtifactTypesNode(
      collection.artifactTypeName,
      artifact.entityName,
      defaultProjectName
    );
  }, [collection.artifactTypeName, artifact.entityName, defaultProjectName]);
  const artifactTypeNode = useNodeValue(typesNode as any);
  const projectsWithArtifactType =
    artifactTypeNode.result?.filter(
      (value: {id: string; name: string}) => value != null
    ) ?? [];
  const hasLoggedVersions = projectsWithArtifactType.length > 0;

  const entityProjects = useNodeValue(
    useMemo(
      () => getEntityProjects(artifact.entityName, defaultProjectName),
      [artifact.entityName, defaultProjectName]
    )
  );

  // Convert list of projects into list of project-options for SelectProject
  // Using the SelectProject component in case we try to move to cross-entity linking
  // in the future
  const projectOptions: ProjectOption[] = entityProjects.result?.projects?.map(
    project => {
      return {
        value: project.name,
        entityName: artifact.entityName,
        projectId: project.id,
        projectName: project.name,
      };
    }
  );
  const entityOptions: EntityOptions[] = [
    {
      label: artifact.entityName,
      options: projectOptions,
    },
  ];
  const onSelectProject = useCallback(
    (option: ProjectOption) => {
      setArtifact({
        ...artifact,
        projectName: option.projectName,
        sequenceName: '',
        alias: '',
        id: '',
      });
    },
    [artifact, setArtifact]
  );
  const onSelectEntity = useCallback(
    (value: string) => {
      setArtifact({
        ...artifact,
        entityName: value,
        projectName: '',
        sequenceName: '',
        alias: '',
        id: '',
      });
    },
    [artifact, setArtifact]
  );

  // Accumulate options for the artifact collection and version dropdown
  const projectArtifactSequences = useNodeValue(
    useMemo(
      () =>
        getProjectArtifactSequences(
          artifact.entityName,
          artifact.projectName ?? '',
          collection.artifactTypeName
        ),
      [artifact.entityName, artifact.projectName, collection.artifactTypeName]
    )
  );
  const artifactSequences = projectArtifactSequences?.result?.artifactSequences;
  const artifactSequenceOptions: ArtifactCollectionOption[] = useMemo(() => {
    return (
      artifactSequences?.map(artifactSequence => {
        return {
          value: artifactSequence.name,
          name: artifactSequence.name,
          artifactType: collection.artifactTypeName,
          collectionType: 'sequence',
        };
      }) ?? []
    );
  }, [artifactSequences, collection.artifactTypeName]);
  const groupedArtifactSequenceOptions =
    artifactSequenceOptions.length !== 0
      ? [
          {
            label: '',
            options: artifactSequenceOptions,
          },
        ]
      : [];

  const onSelectArtifactVersion = useCallback(
    (option: ArtifactVersionOption) => {
      setArtifact({
        ...artifact,
        alias: option.value,
        id: option.artifactVersion.versionId,
        versionIndex: option.artifactVersion.versionIndex,
        ttlDurationSeconds: option.artifactVersion.ttlDurationSeconds,
      });
    },
    [artifact, setArtifact]
  );

  const onSelectArtifact = useCallback(
    (option: ArtifactCollectionOption) => {
      const versions = artifactSequences?.find(
        sequence => sequence?.name === option?.name
      )?.versions as ArtifactVersion[];
      setArtifactVersions(versions);
      // Set default alias/version only when the user selects the artifact sequence for the first time
      const emptyArtifactVersion: ArtifactVersion = {
        versionId: '',
        versionIndex: -1,
        aliases: [],
      };
      let defaultVersionOption: ArtifactVersionOption = {
        label: '',
        value: '',
        artifactVersion: emptyArtifactVersion,
      };
      const defaultVersion = versions?.find(version =>
        version?.aliases.includes('latest')
      );
      if (defaultVersion) {
        defaultVersionOption = {
          label: 'latest',
          value: 'latest',
          artifactVersion: defaultVersion,
        };
      } else {
        // Set the default alias as the highest version number
        const maxVersion = _.maxBy(versions, 'versionIndex');
        if (maxVersion) {
          defaultVersionOption = {
            label: `v${maxVersion.versionIndex}`,
            value: `v${maxVersion.versionIndex}`,
            artifactVersion: maxVersion,
          };
        }
      }
      if (
        defaultVersionOption.artifactVersion.versionId !== '' &&
        defaultVersionOption.value !== ''
      ) {
        setArtifact({
          ...artifact,
          sequenceName: option?.name,
          alias: defaultVersionOption.value,
          id: defaultVersionOption.artifactVersion.versionId,
          ttlDurationSeconds:
            defaultVersionOption.artifactVersion.ttlDurationSeconds,
        });
      } else {
        setArtifact({...artifact, sequenceName: option?.name});
      }
    },
    [artifact, artifactSequences, setArtifact, setArtifactVersions]
  );

  const [isEmptyProject, setIsEmptyProject] = useState(false);
  useEffect(() => {
    if (!projectArtifactSequences.loading) {
      if (artifact.projectName !== '' && artifactSequenceOptions.length === 0) {
        setIsEmptyProject(true);
      } else {
        setIsEmptyProject(false);
      }
      if (artifactSequenceOptions.length > 0 && artifact.sequenceName === '') {
        onSelectArtifact(artifactSequenceOptions[0]);
      }
    }
  }, [
    projectOptions,
    projectArtifactSequences.loading,
    artifact.projectName,
    artifact.sequenceName,
    artifactSequenceOptions,
    setIsEmptyProject,
    onSelectArtifact,
  ]);

  if (artifactTypeNode.loading) {
    return (
      <InstrumentedLoader name="create-registered-collection-has-logged-artifacts" />
    );
  }

  const loggedVersions =
    hasLoggedVersions && entityProjects.result?.projects?.length > 0;
  return (
    <>
      {!hideStepHeader && (
        <S.PageLabelRow>
          Step 2 of 2 - Link a {collection.artifactTypeName} version
        </S.PageLabelRow>
      )}
      {isOrgRegistry && (
        <>
          <S.LabelRow>
            <S.Label>Team</S.Label>
          </S.LabelRow>
          <SelectEntity
            includeAll={false}
            value={artifact.entityName}
            onSelectEntity={onSelectEntity}
            includePersonalEntity={false}
          />
        </>
      )}
      {entityProjects.loading ? (
        <div style={{textAlign: 'center'}}>
          <Loader active inline size="tiny" name="entity-projects" />
        </div>
      ) : (
        <>
          {artifact.entityName !== '' && (
            <AddVersionHelpBanner
              hasLoggedVersions={loggedVersions}
              artifactTypeName={collection.artifactTypeName}
              hideSkipBanner={hideBanner}
            />
          )}
          {loggedVersions && artifact.entityName !== '' && (
            <>
              <S.LabelRow>
                <S.Label>Project</S.Label>
              </S.LabelRow>
              <SelectProject
                defaultEntityName={artifact.entityName}
                defaultProjectName={artifact.projectName}
                options={entityOptions}
                onSelectProject={onSelectProject}
                errorState={isEmptyProject}
              />
              {isEmptyProject && (
                <SubText isError={true}>
                  {`You haven't logged any ${collection.artifactTypeName} artifacts in this project.`}
                </SubText>
              )}
              {projectArtifactSequences.loading ? (
                <div style={{textAlign: 'center', marginTop: '4px'}}>
                  <Loader
                    active
                    inline
                    size="tiny"
                    name="project-artifact-sequences"
                  />
                </div>
              ) : (
                <>
                  <S.LabelRow>
                    <S.Label>{`${capitalizeFirstLetter(
                      collection.artifactTypeName
                    )} Artifact`}</S.Label>
                  </S.LabelRow>
                  <SelectArtifactCollection
                    options={groupedArtifactSequenceOptions}
                    defaultCollectionValue={artifact.sequenceName}
                    disabled={
                      artifact.projectName === '' ||
                      artifactSequenceOptions?.length === 0
                    }
                    onSelectArtifactCollection={onSelectArtifact}
                    setFirstOptionAsDefault={true}
                  />
                  <S.LabelRow>
                    <S.Label>Version</S.Label>
                  </S.LabelRow>
                  <SelectArtifactCollectionVersion
                    artifactVersions={artifactVersions}
                    selectedAlias={artifact.alias}
                    includeAll={false}
                    onSelectArtifactAlias={onSelectArtifactVersion}
                    disabled={
                      artifact.sequenceName === '' ||
                      artifactVersions.length === 0
                    }
                  />
                </>
              )}

              <S.LinkCollectionNamePanel>
                <Icon name="versions-layers" style={{marginRight: '4px'}} />
                {/*The index of the new version = # of versions linked so far*/}
                Registry version - {collection.name}:v{collection.numVersions}
              </S.LinkCollectionNamePanel>
              <ArtifactVersionTTLBanner artifact={artifact} />
            </>
          )}
        </>
      )}
    </>
  );
};
